import React, { useEffect, useState } from "react";
import { customFontStorage, CustomFontMetadata } from "../../services/customFontStorage";
import { s3CustomFontService, FontCDNData } from "../../services/s3CustomFontService";

interface SimpleFontManagerProps {
  isVisible: boolean;
  onClose: () => void;
}

const SimpleFontManager: React.FC<SimpleFontManagerProps> = ({ isVisible, onClose }) => {
  const [localFonts, setLocalFonts] = useState<CustomFontMetadata[]>([]);
  const [cdnFonts, setCdnFonts] = useState<FontCDNData[]>([]);
  const [loading, setLoading] = useState(false);
  const [localStats, setLocalStats] = useState<{
    totalFonts: number;
    totalSize: number;
    fonts: Array<{ name: string; size: number; uploadedAt: number }>;
  } | null>(null);
  const [cdnStats, setCdnStats] = useState<{
    totalFonts: number;
    totalSize: number;
    fonts: Array<{ name: string; size: number; uploadedAt: number; s3Url: string }>;
  } | null>(null);

  const loadFonts = async () => {
    setLoading(true);
    try {
      // Load local fonts
      const [fontMetadata, storageStats] = await Promise.all([
        customFontStorage.getCustomFontsMetadata(),
        customFontStorage.getStorageStats(),
      ]);
      setLocalFonts(fontMetadata);
      setLocalStats(storageStats);

      // Load S3 CDN fonts
      try {
        const cdnFontStats = await s3CustomFontService.getFontCDNStats();
        setCdnStats(cdnFontStats);
        const cdnFontRegistry = await s3CustomFontService.getLocalFontRegistry();
        setCdnFonts(cdnFontRegistry);
      } catch (error) {
        console.error("Error loading S3 CDN fonts:", error);
      }
    } catch (error) {
      console.error("Error loading custom fonts:", error);
    }
    setLoading(false);
  };

  const handleDeleteLocalFont = async (fontName: string) => {
    if (window.confirm(`Are you sure you want to delete the local font "${fontName}"?`)) {
      try {
        await customFontStorage.deleteCustomFont(fontName);

        // Remove font from document.fonts if it exists
        const existingFont = Array.from(document.fonts).find(
          (font) => font.family.toLowerCase() === fontName.toLowerCase()
        );
        if (existingFont) {
          document.fonts.delete(existingFont);
        }

        // Reload the font list
        await loadFonts();

        alert(`Local font "${fontName}" deleted successfully!`);
      } catch (error) {
        console.error("Error deleting local font:", error);
        alert(`Failed to delete local font "${fontName}"`);
      }
    }
  };

  const handleDeleteCdnFont = async (fontName: string) => {
    if (window.confirm(`Are you sure you want to remove the CDN font "${fontName}" from your registry?`)) {
      try {
        await s3CustomFontService.removeFontFromRegistry(fontName);

        // Remove font from document.fonts if it exists
        const existingFont = Array.from(document.fonts).find(
          (font) => font.family.toLowerCase() === fontName.toLowerCase()
        );
        if (existingFont) {
          document.fonts.delete(existingFont);
        }

        // Reload the font list
        await loadFonts();

        alert(`CDN font "${fontName}" removed from registry successfully!`);
      } catch (error) {
        console.error("Error removing CDN font:", error);
        alert(`Failed to remove CDN font "${fontName}"`);
      }
    }
  };

  const handleClearLocalFonts = async () => {
    if (window.confirm("Are you sure you want to delete ALL local custom fonts? This action cannot be undone.")) {
      try {
        await customFontStorage.clearAllCustomFonts();
        await loadFonts();
        alert("All local custom fonts deleted successfully!");
      } catch (error) {
        console.error("Error clearing local fonts:", error);
        alert("Failed to clear local fonts");
      }
    }
  };

  const handleClearCdnRegistry = async () => {
    if (window.confirm("Clear CDN font registry? This will remove font references but not delete files from S3.")) {
      try {
        await s3CustomFontService.clearFontRegistry();
        await loadFonts();
        alert("CDN font registry cleared successfully!");
      } catch (error) {
        console.error("Error clearing CDN registry:", error);
        alert("Failed to clear CDN registry");
      }
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString() + " " + new Date(timestamp).toLocaleTimeString();
  };

  useEffect(() => {
    if (isVisible) {
      loadFonts();
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        background: "white",
        border: "2px solid #ccc",
        borderRadius: "8px",
        padding: "20px",
        maxWidth: "700px",
        maxHeight: "80vh",
        overflow: "auto",
        zIndex: 10000,
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
      }}
    >
      <div style={{ marginBottom: "15px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, color: "#333" }}>🎨 Font CDN Manager</h3>
        <button
          onClick={onClose}
          style={{
            background: "#f44336",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "5px 10px",
            cursor: "pointer",
          }}
        >
          ✕ Close
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>Loading fonts...</div>
      ) : (
        <>
          {/* S3 CDN Fonts Section */}
          <div style={{ marginBottom: "20px" }}>
            <h4 style={{ color: "#2196f3", marginBottom: "10px" }}>🌐 S3 Font CDN</h4>
            {cdnStats && (
              <div style={{ marginBottom: "15px", padding: "10px", background: "#e3f2fd", borderRadius: "4px" }}>
                <p style={{ margin: "5px 0" }}>CDN Fonts Available: {cdnStats.totalFonts}</p>
                <p style={{ margin: "5px 0" }}>Total Storage: {formatBytes(cdnStats.totalSize)}</p>
                <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
                  These fonts work across all devices and are cached by browsers
                </p>
              </div>
            )}

            {cdnFonts.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "15px",
                  color: "#666",
                  background: "#f9f9f9",
                  borderRadius: "4px",
                }}
              >
                No CDN fonts available. Upload a font to get started!
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "10px" }}>
                  <button
                    onClick={handleClearCdnRegistry}
                    style={{
                      background: "#ff9800",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    🧹 Clear Registry
                  </button>
                  <span style={{ marginLeft: "10px", fontSize: "12px", color: "#666" }}>
                    (Removes from UI, files stay in S3)
                  </span>
                </div>

                <div style={{ maxHeight: "250px", overflow: "auto", marginBottom: "10px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#e3f2fd" }}>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #90caf9" }}>Font Name</th>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #90caf9" }}>Size</th>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #90caf9" }}>Uploaded</th>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #90caf9" }}>CDN URL</th>
                        <th style={{ padding: "8px", textAlign: "center", border: "1px solid #90caf9" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cdnFonts.map((font, index) => (
                        <tr key={font.name} style={{ background: index % 2 === 0 ? "#f3f8ff" : "white" }}>
                          <td style={{ padding: "8px", border: "1px solid #90caf9", fontFamily: font.name }}>
                            {font.name}
                          </td>
                          <td style={{ padding: "8px", border: "1px solid #90caf9" }}>{formatBytes(font.fileSize)}</td>
                          <td style={{ padding: "8px", border: "1px solid #90caf9", fontSize: "12px" }}>
                            {formatDate(font.uploadedAt)}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #90caf9",
                              fontSize: "10px",
                              maxWidth: "150px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <a
                              href={font.s3Url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#2196f3", textDecoration: "none" }}
                            >
                              {font.s3Url.split("/").pop()}
                            </a>
                          </td>
                          <td style={{ padding: "8px", border: "1px solid #90caf9", textAlign: "center" }}>
                            <button
                              onClick={() => handleDeleteCdnFont(font.name)}
                              style={{
                                background: "#f44336",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                padding: "4px 8px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              🗑️ Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Local Fonts Section */}
          <div style={{ marginBottom: "15px" }}>
            <h4 style={{ color: "#ff9800", marginBottom: "10px" }}>💾 Local Fonts (Fallback)</h4>
            {localStats && (
              <div style={{ marginBottom: "15px", padding: "10px", background: "#fff3e0", borderRadius: "4px" }}>
                <p style={{ margin: "5px 0" }}>Local Custom Fonts: {localStats.totalFonts}</p>
                <p style={{ margin: "5px 0" }}>Local Storage Used: {formatBytes(localStats.totalSize)}</p>
                <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>These fonts only work on this device</p>
              </div>
            )}

            {localFonts.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "15px",
                  color: "#666",
                  background: "#f9f9f9",
                  borderRadius: "4px",
                }}
              >
                No local fonts stored.
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "15px" }}>
                  <button
                    onClick={handleClearLocalFonts}
                    style={{
                      background: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "8px 15px",
                      cursor: "pointer",
                    }}
                  >
                    🗑️ Clear All Local Fonts
                  </button>
                </div>

                <div style={{ maxHeight: "200px", overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#fff3e0" }}>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #ffcc02" }}>Font Name</th>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #ffcc02" }}>Size</th>
                        <th style={{ padding: "8px", textAlign: "left", border: "1px solid #ffcc02" }}>Uploaded</th>
                        <th style={{ padding: "8px", textAlign: "center", border: "1px solid #ffcc02" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localFonts.map((font, index) => {
                        const fontStats = localStats?.fonts.find((f) => f.name === font.name);
                        return (
                          <tr key={font.name} style={{ background: index % 2 === 0 ? "#fffbf0" : "white" }}>
                            <td style={{ padding: "8px", border: "1px solid #ffcc02", fontFamily: font.name }}>
                              {font.name}
                            </td>
                            <td style={{ padding: "8px", border: "1px solid #ffcc02" }}>
                              {fontStats ? formatBytes(fontStats.size) : "N/A"}
                            </td>
                            <td style={{ padding: "8px", border: "1px solid #ffcc02", fontSize: "12px" }}>
                              {formatDate(font.uploadedAt)}
                            </td>
                            <td style={{ padding: "8px", border: "1px solid #ffcc02", textAlign: "center" }}>
                              <button
                                onClick={() => handleDeleteLocalFont(font.name)}
                                style={{
                                  background: "#f44336",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "3px",
                                  padding: "4px 8px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Usage Instructions */}
          <div
            style={{
              marginTop: "20px",
              padding: "15px",
              background: "#f8f9fa",
              borderRadius: "4px",
              borderLeft: "4px solid #2196f3",
            }}
          >
            <h4 style={{ color: "#333", marginBottom: "10px" }}>💡 How it works</h4>
            <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "14px", color: "#555" }}>
              <li>
                <strong>Upload:</strong> Fonts go to S3 CDN (works across all devices)
              </li>
              <li>
                <strong>Access:</strong> Anyone opening the app will load all available CDN fonts
              </li>
              <li>
                <strong>Export:</strong> HTML references S3 URLs directly (fast & reliable)
              </li>
              <li>
                <strong>Sharing:</strong> All uploaded fonts are automatically shared across devices
              </li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default SimpleFontManager;
